import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { Services } from '@project/API';
import { SingleWindow } from '../Window';
import {
  IUserLayerRecord,
  IUserLayerRecordProperties,
} from '@project/API/resources/userLayer';
import axios from 'axios';
import { Query, QueryOptions } from '../Query';
import { store } from '..';
import { UserLayerCategory } from '@project/store/UserLayers/UserLayers';

export type UserLayerFilteredFields = 'is_checked' | 'is_archived' | 'category';

const DEFAULT_QUERY: QueryOptions<UserLayerFilteredFields> = {
  page: 1,
  pageSize: 10,
  search: undefined,
  filter: {
    orParams: [
      [
        { field: 'is_checked', value: 'true' },
        { field: 'is_archived', value: 'false' },
      ],
    ],
  },
};

export class SelectedUserLayer {
  private _services: Services;
  private _recordsLoaderSignals = new Set<AbortController>();

  readonly userLayerRecordCard = new SingleWindow<IUserLayerRecordProperties>();

  @observable query: Query<UserLayerFilteredFields>;
  @observable records: IUserLayerRecord[] = [];
  @observable private _layerItem: UserLayerCategory | null;
  @observable private _isLoading = false;
  @observable private _error?: string = undefined;
  @observable private _totalRecordsCount = 0;

  constructor(services: Services, layerId?: UserLayerCategory) {
    this._services = services;
    this._layerItem = layerId || null;
    this.query = new Query<UserLayerFilteredFields>(DEFAULT_QUERY);

    makeObservable<SelectedUserLayer>(this);
  }

  @computed get totalRecordsCount() {
    return this._totalRecordsCount;
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  @computed get error() {
    return this._error;
  }

  @computed get layerItem() {
    return this._layerItem;
  }

  @action setSelectedLayer = (layer: UserLayerCategory) => {
    this.query.filter.reset();
    this.query.resetSearch();
    this.query.filter.setAndParams([
      { field: 'category', value: layer.id.toString() },
    ]);
    runInAction(() => {
      this._layerItem = layer;
    });
  };

  @action clearSelectedLayer = () => {
    this.query.filter.reset();
    store.marker.remove();
    runInAction(() => {
      this._layerItem = null;
    });
  };

  @action resetFilters = () => {
    this.query.filter.reset();
    this._layerItem &&
      this.query.filter.setAndParams([
        { field: 'category', value: this._layerItem.id.toString() },
      ]);
  };

  @action loadRecords = async (params?: URLSearchParams) => {
    if (this._isLoading) {
      this._recordsLoaderSignals.forEach((s) => s.abort());
      this._recordsLoaderSignals = new Set();
    }

    const currentSignal = new AbortController();
    this._recordsLoaderSignals.add(currentSignal);

    runInAction(() => {
      this._isLoading = true;
      this._error = undefined;
    });

    try {
      const result = await this._services.userLayerRecords.get(
        params,
        currentSignal.signal,
      );

      runInAction(() => {
        this.records = result.results.features;
        this._totalRecordsCount = result.count;
      });
    } catch (e) {
      if (!axios.isCancel(e)) {
        runInAction(() => {
          this.records = [];
          this._error = 'Произошла ошибка при загрузке данных';
        });
      }
    } finally {
      this._recordsLoaderSignals.delete(currentSignal);

      runInAction(() => {
        this._isLoading = this._recordsLoaderSignals.size > 0;
      });
    }
  };

  static isFilteredFields = (
    value: string,
  ): value is UserLayerFilteredFields => {
    return ['is_checked', 'is_archived'].some((v) => value === v);
  };
}
